/**
 * The clip as the edit surfaces see it — one shape whether the payload was a
 * live in-memory Job (SSE / GET while running) or a persisted JobListItem.
 * Moved out of JobDetail so the Studio's Edit bar can fetch the same view
 * (spec 2026-09-16 §2.4).
 */
import type { Job, JobInput, JobListItem, JobLogLine, JobStatus } from "../../api";

export interface JobView {
  id: string;
  status: JobStatus;
  progress: number;
  currentStage?: string;
  errorMsg?: string;
  hasVideo: boolean;
  hasCaption: boolean;
  hasTranscript: boolean;
  /** Display title resolved by the server (never the raw id) when it carries
   *  one; the page falls back to `sourceTitle`, then "Untitled video" (`displayTitle`). */
  title?: string;
  sourceTitle?: string;
  sourceUrl?: string;
  speaker?: string;
  durationSec?: number;
  createdAt: number;
  logs: JobLogLine[];
  finishedAt?: number;
  /** Full submitted input (URL, timestamps, host/guests/topic). Comes
   * from the live `Job` payload for in-flight jobs, hydrated from
   * input.json by the server for persisted jobs. */
  input?: JobInput;
}

function isLiveJob(j: unknown): j is Job {
  return typeof j === "object" && j !== null && "logs" in j;
}

/** Artefact flags the SERVER attached to a live job (GET /api/jobs/:id reads
 *  them off disk). Raw SSE payloads are plain in-memory Jobs and carry none,
 *  so the fields are optional and absent means "unknown", never "false" —
 *  assuming false is what hid the whole Regenerate block. */
export type LiveJobWithArtefacts = Job & Partial<{
  hasVideo: boolean;
  hasCaption: boolean;
  hasTranscript: boolean;
}>;

export function toView(payload: Job | JobListItem): JobView {
  if (isLiveJob(payload)) {
    const live = payload as LiveJobWithArtefacts;
    return {
      id: payload.id,
      status: payload.status,
      progress: payload.progress,
      currentStage: payload.currentStage,
      errorMsg: payload.errorMsg,
      hasVideo: live.hasVideo ?? payload.status === "ready",
      hasCaption: live.hasCaption ?? false,
      hasTranscript: live.hasTranscript ?? false,
      title: payload.title,
      sourceTitle: payload.sourceTitle,
      sourceUrl: payload.sourceUrl ?? payload.input.url,
      // Prefer the server-resolved speaker (honours operator-speaker.json and
      // resolveSpeaker's render decision); the guests/host slug guess is only
      // the cold fallback for payloads that don't carry it (raw SSE updates).
      speaker: payload.speaker ?? payload.input.guests?.[0] ?? payload.input.host,
      createdAt: payload.createdAt,
      logs: payload.logs,
      finishedAt: payload.finishedAt,
      input: payload.input,
    };
  }
  return {
    id: payload.id,
    status: payload.status,
    progress: payload.progress,
    hasVideo: payload.hasVideo,
    hasCaption: payload.hasCaption,
    hasTranscript: payload.hasTranscript,
    title: payload.title,
    sourceTitle: payload.sourceTitle,
    sourceUrl: payload.sourceUrl,
    speaker: payload.speaker,
    durationSec: payload.durationSec,
    createdAt: payload.createdAt,
    logs: [],
    input: payload.input,
  };
}

/** Terminal = nothing is running for this clip right now. */
export function isTerminalStatus(status: JobStatus): boolean {
  return status === "ready" || status === "failed" || status === "incomplete";
}

/**
 * What the headline says. The server's display title wins (it already
 * derives one from the source title, the speaker + first words, or a
 * humanised id — see jobStore.displayTitle); the source title is next; and
 * the last resort is a plain "Untitled video". The raw id NEVER headlines a
 * page — that is the operator's rule, and the reason this has no id branch.
 */
export function displayTitle(view: Pick<JobView, "title" | "sourceTitle">): string {
  const t = view.title?.trim();
  if (t) return t;
  const s = view.sourceTitle?.trim();
  if (s) return s;
  return "Untitled video";
}
